import { atom, useAtomValue } from "jotai";
import { atomFamily, atomWithReset, atomWithStorage, RESET } from "jotai/utils";
import type { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";

// Only a resolved domain user owns settings; Firebase identifies the base login.
export const userStorageScopeAtom = atom<string | null>(null);

export const getUserStoragePrefix = (tenantId: string, userId: string) =>
  `jm:user:${encodeURIComponent(tenantId)}:${encodeURIComponent(userId)}:`;

export function useUserStorageKey(key: string | null) {
  const scope = useAtomValue(userStorageScopeAtom);
  return scope && key ? scope + key : null;
}

export function atomWithUserStorage<Value>(
  key: string,
  initialValue: Value,
  storage?: SyncStorage<Value>,
  options = { getOnInit: true },
) {
  const unresolvedAtom = atomWithReset(initialValue);
  const scopedAtoms = atomFamily((scope: string) =>
    atomWithStorage(scope + key, initialValue, storage, options),
  );
  return atom(
    (get) => {
      const scope = get(userStorageScopeAtom);
      return get(scope ? scopedAtoms(scope) : unresolvedAtom);
    },
    (get, set, update: Value | typeof RESET | ((value: Value) => Value | typeof RESET)) => {
      const scope = get(userStorageScopeAtom);
      set(scope ? scopedAtoms(scope) : unresolvedAtom, update);
    },
  );
}
